import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { TooltipWrapper } from 'uiComponents/tooltip.new'
import { LucideIcon, LucideIconName } from 'utils/lucideIcon'
import './rowConfirmActions.scss'

export interface RowConfirmAction {
    key: string
    iconName: LucideIconName
    label: string
    confirmLabel: string
    tone?: 'neutral' | 'destructive'
    onConfirm: () => void
}

interface RowConfirmActionsProps {
    actions: RowConfirmAction[]
}

export const RowConfirmActions: React.FC<RowConfirmActionsProps> = ({ actions }) => {
    const [armedKey, setArmedKey] = useState<string | null>(null)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!armedKey) {
            return
        }
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setArmedKey(null)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [armedKey])

    const handleClick = (action: RowConfirmAction) => {
        if (armedKey === action.key) {
            action.onConfirm()
            setArmedKey(null)
        } else {
            setArmedKey(action.key)
        }
    }

    return (
        <div ref={ref} className={classNames('row-confirm-actions', { 'row-confirm-actions--armed': !!armedKey })}>
            {actions.map((action) => {
                const armed = armedKey === action.key
                const hidden = !!armedKey && !armed
                return (
                    <TooltipWrapper
                        key={action.key}
                        text={armed ? undefined : action.label}
                        position="top"
                        arrowPosition="right"
                        className={classNames('row-confirm-slot', {
                            'row-confirm-slot--armed': armed,
                            'row-confirm-slot--hidden': hidden,
                        })}
                    >
                        <button
                            type="button"
                            aria-label={action.label}
                            aria-hidden={hidden}
                            tabIndex={hidden ? -1 : 0}
                            className={classNames(
                                'row-confirm-button',
                                `row-confirm-button--${action.tone ?? 'neutral'}`,
                                { 'row-confirm-button--armed': armed },
                            )}
                            onClick={() => handleClick(action)}
                        >
                            <LucideIcon iconName={action.iconName} size={16} />
                            {armed && <span className="row-confirm-label">{action.confirmLabel}</span>}
                        </button>
                    </TooltipWrapper>
                )
            })}
        </div>
    )
}
